/*
 * Quick search over the hubs' links (the phone menu and each hub page).
 *
 * A link is found by its label in the language on screen, by its English and
 * its Hebrew label whichever language is on screen (people at the bench say
 * "Import" in Hebrew too), and by its registry aliases. Matching ignores case,
 * accents and Hebrew points, and every word typed has to match somewhere.
 *
 * The other language's labels come from searchTerms.json, not from its
 * catalogue: a catalogue is one lazy chunk per language (src/i18n/index.js),
 * and importing the English one here would download all of it for every
 * Hebrew reader. search.test.js holds searchTerms.json equal to the catalogues.
 */
import TERMS from './searchTerms.json';
import { visibleHubs } from './registry';

/** Lower case, no accents or Hebrew points, no quote marks, one space between words. */
export function normalise(text) {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/['"`׳״‘’“”]/g, '')
    .replace(/[-_/·,.()]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Every text a link may be found by. */
export function itemTerms(item, t) {
  const labels = TERMS[item.label] || {};
  return [t ? t(item.label) : null, labels.en, labels.he, ...(item.aliases || [])]
    .filter((term) => typeof term === 'string' && term);
}

/**
 * The links this person may follow that match `query`, as {hubKey, sectionKey,
 * item}, in menu order. Across hubs a page listed twice is returned once (the
 * first hub's); `hubKey` keeps to one hub. An empty query finds nothing.
 */
export function searchLinks(user, query, t, { hubKey } = {}) {
  const words = normalise(query).split(' ').filter(Boolean);
  if (!words.length) return [];
  const seen = new Set();
  const found = [];
  for (const hub of visibleHubs(user)) {
    if (hubKey && hub.key !== hubKey) continue;
    for (const section of hub.sections) {
      for (const item of section.items) {
        if (seen.has(item.to)) continue;
        const haystack = normalise(itemTerms(item, t).join(' '));
        if (words.every((word) => haystack.includes(word))) {
          seen.add(item.to);
          found.push({ hubKey: hub.key, sectionKey: section.key, item });
        }
      }
    }
  }
  return found;
}
